/**
 * DCMS Loader - Module for handling loading in iframes
 *
 * In iframe mode: sends a postMessage to the parent on navigation (pagination, sorting,
 * beforeunload) — the parent decides how to display it (inline loader in search.tpl).
 *
 * The modal spinner (showModalSpinner/hideModalSpinner) is available ONLY for manual
 * invocation — it is NOT triggered automatically.
 *
 * @author AMPD Sp. z o.o.
 * @version 4.0
 */

class DCMSLoader {
    constructor() {
        this.isInIframe = window.self !== window.top;
        this.init();
    }

    /**
     * Initialization
     */
    init() {
        if (this.isInIframe) {
            this.initIframeMode();
        } else {
            this.initParentMode();
        }
        this.initOverlayWatchdog();
        this.initSkeletonHost();
        this.initBackForwardRestore();
    }

    /**
     * Page restored from the back/forward cache (pageshow with event.persisted), e.g. the
     * browser's Back button after a link that opened another page in the main window.
     *
     * The browser restores the page exactly as it was left: beforeunload had painted
     * #dcmsPageLoadingOverlay (and sent 'show', which hides the results frame of the search
     * page), and no DOMContentLoaded follows to remove it — the page stayed covered until the
     * 15 s watchdog. Every frame of the restored page gets its own pageshow, so each one
     * clears what it owns: the overlay in its own document, and its parent's loading state
     * through the standard 'hide' message (search.tpl shows the results frame again).
     */
    initBackForwardRestore() {
        window.addEventListener('pageshow', (event) => {
            if (!event.persisted) return;
            // Leaving the page again must show the overlay as usual
            this.isClosingModal = false;
            var overlay = document.getElementById('dcmsPageLoadingOverlay');
            if (overlay && overlay.parentNode) {
                overlay.parentNode.removeChild(overlay);
            }
            if (this.isInIframe) {
                this.notifyParent('hide');
            }
            DCMSLoader.log('Page restored from the back/forward cache — loading state cleared');
        });
    }

    /**
     * Watchdog for an orphaned #dcmsPageLoadingOverlay — runs in the window that the
     * child iframe paints the overlay into (and sends 'show' to on beforeunload).
     *
     * The overlay is removed by removePageOverlay() on the DOMContentLoaded of the
     * NEXT page in the iframe — if the response doesn't include dcms-loader.js (a bare
     * document, server timeout), the overlay would hang forever and block the page
     * (pointer-events:all).
     *
     * The timer CANNOT be registered by the iframe window being unmounted (even
     * win.setTimeout on the parent window) — the browser invalidates callbacks from a
     * destroyed realm and the timer never fires. That's why the watchdog lives here,
     * in the host window's realm: a 'show' message arms it, 'hide' disarms it, and
     * after 15 s without 'hide' the overlay is removed unconditionally. Registered in
     * both modes — a page inside an iframe can itself host its own iframes (e.g. modals).
     *
     * Downloads: a same-frame link, form or location change whose response is a file
     * (Content-Disposition) fires beforeunload, but the page is never unloaded — the browser
     * downloads the file or shows the save dialog. The server marks such responses with a new
     * value of the dcms_download cookie (AMPD\DCMS2\HTTP\DownloadMarker); while the overlay is
     * armed the watchdog polls it and, once it changes, clears the loading state at once
     * through the standard 'hide' message (search.tpl restores the results frame too).
     */
    initOverlayWatchdog() {
        var timer = null;
        var downloadPoll = null;
        window.addEventListener('message', function (event) {
            var d = event.data;
            if (!d || d.type !== 'dcms-loader') return;
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            if (downloadPoll) {
                clearInterval(downloadPoll);
                downloadPoll = null;
            }
            if (d.action === 'show') {
                timer = setTimeout(function () {
                    timer = null;
                    if (downloadPoll) {
                        clearInterval(downloadPoll);
                        downloadPoll = null;
                    }
                    var o = document.getElementById('dcmsPageLoadingOverlay');
                    if (o && o.parentNode) {
                        o.parentNode.removeChild(o);
                        DCMSLoader.log('Watchdog removed stuck page loading overlay');
                    }
                }, 15000);
                var marker = DCMSLoader.downloadMarker();
                downloadPoll = setInterval(function () {
                    if (DCMSLoader.downloadMarker() === marker) return;
                    clearInterval(downloadPoll);
                    downloadPoll = null;
                    var o = document.getElementById('dcmsPageLoadingOverlay');
                    if (o && o.parentNode) {
                        o.parentNode.removeChild(o);
                    }
                    DCMSLoader.log('File response (download) — page loading overlay removed');
                    // Standard 'hide' (also clears the timer above and a pending skeleton)
                    try {
                        window.postMessage({ type: 'dcms-loader', action: 'hide' }, window.location.origin);
                    } catch (e) { /* ignore */ }
                }, 250);
            }
        });
    }

    /**
     * Current value of the download marker cookie (AMPD\DCMS2\HTTP\DownloadMarker::COOKIE),
     * '' when not set. Each file response sets a new value.
     */
    static downloadMarker() {
        var m = document.cookie.match(/(?:^|;\s*)dcms_download=([^;]*)/);
        return m ? m[1] : '';
    }

    /**
     * PARENT WINDOW mode — no automatic spinner.
     * The inline loader is handled directly in search.tpl.
     */
    initParentMode() {
        DCMSLoader.log('Initializing parent mode');
    }

    /**
     * IFRAME mode — sends notifications to the parent on navigation
     */
    initIframeMode() {
        DCMSLoader.log('Initializing iframe mode');

        // Flag — don't send 'show' when closing a modal / recordset refresh
        this.isClosingModal = false;

        this.installHeightCalculation();

        document.addEventListener('DOMContentLoaded', () => {
            this.enableScrollIfNoIframeResizer();
            this.resetParentScroll();
            this.interceptPaginationClicks();
            this.interceptSortingClicks();
            this.initInlineLoadingOverlay();
            this.interceptDeferredEditClicks();

            // Deferred-render page: keep the parent skeleton overlay up until the
            // form is fully initialised (window.load), so it also hides the
            // progressive component init — not just the blank/server phase. A hard
            // timeout and the Escape key guard against a component that never
            // finishes loading. dismiss() is one-shot: a stray late timer or
            // keypress must never touch the overlay of a LATER navigation (e.g.
            // the spinner painted when the user saves the form).
            if (window.__dcmsDeferred) {
                var self = this;
                var dismissed = false;
                var fallbackTimer = null;
                var dismiss = function () {
                    if (dismissed) return;
                    dismissed = true;
                    if (fallbackTimer) clearTimeout(fallbackTimer);
                    // Correct the iframe height WHILE the skeleton is still visible
                    // (drop the carry-over, catch up with a growing editor) and only
                    // once the content is stable — remove the overlay. This way the
                    // height revealed is already correct, with no jump/flicker
                    // (see resyncDeferredHeight).
                    self.resyncDeferredHeight(function () {
                        self.notifyParent('hide');
                        self.removePageOverlay();
                    });
                };
                if (document.readyState === 'complete') {
                    dismiss();
                } else {
                    window.addEventListener('load', dismiss, { once: true });
                }
                fallbackTimer = setTimeout(dismiss, 12000);
                document.addEventListener('keydown', function (e) {
                    if (e.key === 'Escape' || e.key === 'Esc') dismiss();
                });
                return;
            }

            // Notify the parent that the iframe has loaded
            this.notifyParent('hide');
            this.removePageOverlay();

            // Drop any iframe height carry-over after a full document navigation
            // (e.g. long list → short edit form). Deliberately IN THE BACKGROUND,
            // AFTER removing the overlay — navigation must not wait for the
            // height to stabilize (see resyncNavigationHeight).
            this.resyncNavigationHeight();
        });

        // Listen for messages — set a flag to skip the spinner on beforeunload
        window.addEventListener('message', (event) => {
            if (event.data && (event.data.type === 'dcms-modal-closing' || event.data.type === 'dcms:recordset:refresh')) {
                this.isClosingModal = true;
                // A racing beforeunload may have already painted #dcmsPageLoadingOverlay before this
                // flag was set (the flag only suppresses FUTURE shows — it does not clear an overlay
                // that is already on screen). A closing modal has no following DOMContentLoaded to call
                // removePageOverlay(), so the overlay would stay orphaned (pointer-events:all) and
                // freeze the whole page. Clear it here so closing a modal can never leave it stuck.
                this.clearStuckOverlay();
            }
        });

        // Before leaving the page — notify the parent + show the overlay (but not when closing a modal)
        window.addEventListener('beforeunload', () => {
            if (!this.isClosingModal) {
                this.notifyParent('show');
                this.showPageOverlay();
            }
        });
    }

    /**
     * Intercepts clicks on pagination buttons
     */
    interceptPaginationClicks() {
        document.addEventListener('click', (e) => {
            if (e.target.closest('[data-pagination-action="navigate"]')) {
                this.notifyParent('show');
            }
        });

        document.querySelectorAll('[id^="results-per-page"]').forEach(select => {
            select.addEventListener('change', () => {
                this.notifyParent('show');
            });
        });

        document.querySelectorAll('[data-pagination-goto-button]').forEach(button => {
            button.addEventListener('click', () => {
                this.notifyParent('show');
            });
        });
    }

    /**
     * Intercepts clicks on column headers (sorting)
     */
    interceptSortingClicks() {
        document.addEventListener('click', (e) => {
            if (e.target.closest('th[data-dcms-sortable]')) {
                this.notifyParent('show');
            }
        });
    }

    /**
     * Deferred-render: when a page-navigation EDIT link is clicked on a list
     * whose dataset opted in (the backend sets window.__dcmsListDeferred on such
     * lists only), ask the PARENT window to prefetch the structural skeleton and
     * paint it into the loading overlay while the real form loads natively in
     * the iframe.
     *
     * The fetch runs in the parent realm on purpose: this iframe is about to
     * unload, and callbacks registered by a destroyed realm are invalidated by
     * the browser (same reason the overlay watchdog lives in the host window).
     *
     * Backward-compatible gates: without the __dcmsListDeferred flag — the
     * default everywhere — this path is fully inert (no listener work, no fetch,
     * no side effects). Modal-based edit links (data-dcms-modal) never navigate
     * this page, so they are skipped. Never blocks navigation.
     */
    interceptDeferredEditClicks() {
        if (!window.__dcmsListDeferred) return;
        // Bubble phase on purpose: links that cancel their own navigation
        // (locked-edit rows call stopPropagation + preventDefault inline) never
        // reach this listener, so no phantom skeleton is requested for them.
        document.addEventListener('click', (e) => {
            // Only plain left-clicks that will actually navigate THIS page —
            // new-tab/middle/modified clicks leave the list in place, and a
            // prefetched skeleton would then leak into a later navigation.
            if (e.defaultPrevented || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
            var a = e.target.closest && e.target.closest('a[href*="datasetMode=edit"]');
            if (!a || !a.href || a.href.indexOf('dcms_skeleton') > -1) return;
            if (a.hasAttribute('data-dcms-modal') || a.hasAttribute('data-lock-edit')) return;
            var url = a.href + (a.href.indexOf('?') > -1 ? '&' : '?') + 'dcms_skeleton=1';
            // The skeleton must come from the dataset renderer (iframe pipeline),
            // not the Master page wrapper, so force the iframe flag.
            if (url.indexOf('dcmsiframe') === -1) {
                url += '&dcmsiframe=1';
            }
            // The edit page renders in the same iframe and fixed/fluid mode as
            // this list, so THIS page's #dcmsContainer width is exactly the
            // width the form will get — pass it along so the skeleton matches
            // (incl. the container-query driven two-column aside layout).
            var contentWidth = 0;
            var cont = document.getElementById('dcmsContainer');
            if (cont) {
                try { contentWidth = Math.round(cont.getBoundingClientRect().width); } catch (err) {}
            }
            // Own message type (NOT 'dcms-loader'): pre-existing listeners — the
            // overlay watchdog (clears its timer on any dcms-loader message) and
            // search.tpl — must never see this. Explicit same-origin target: the
            // URL carries a row id and must not leak to a cross-origin embedder.
            try {
                if (window.parent && window.parent !== window) {
                    window.parent.postMessage(
                        { type: 'dcms-skeleton', action: 'show-skeleton', url: url, contentWidth: contentWidth },
                        window.location.origin
                    );
                }
            } catch (err) { /* cross-origin parent — skip, plain spinner shows */ }
        });
    }

    /**
     * Skeleton host — runs in the window that hosts child iframes and their
     * loading overlay (the same realm as the overlay watchdog, for the same
     * realm-lifetime reason). Handles the deferred-render skeleton lifecycle:
     *
     *  - 'show-skeleton' {url} → fetch the skeleton fragment (same-origin only)
     *    and paint it into #dcmsPageLoadingOverlay; overlay and fetch may
     *    complete in either order, so painting is attempted on both signals.
     *  - 'show'  → the overlay was just painted; paint a pending skeleton.
     *  - 'hide'  → loading finished; drop any pending skeleton.
     *  - Escape  → dismiss a visible loading overlay early (fallback if a
     *    component hangs); routed through the standard 'hide' message so
     *    search.tpl also restores the content iframe's opacity.
     *
     * With no deferred datasets in use nothing ever sends 'show-skeleton', the
     * pending slot stays empty and every branch below is a no-op.
     */
    initSkeletonHost() {
        var self = this;
        this.hostSkeleton = null;
        this.hostSkeletonExpires = 0;
        // Generation token: bumped whenever the pending skeleton is (re)set or
        // cleared, so a slow in-flight fetch resolving AFTER a 'hide'/Escape can
        // never re-arm a stale skeleton for a later, unrelated navigation.
        this.hostSkeletonGen = 0;

        window.addEventListener('message', function (event) {
            var d = event.data;
            if (!d) return;

            // Deferred-skeleton request — own message type, so pre-existing
            // 'dcms-loader' listeners (overlay watchdog, search.tpl) never see it.
            if (d.type === 'dcms-skeleton' && d.action === 'show-skeleton' && d.url) {
                // Same-origin guard — never fetch a URL posted by a foreign frame.
                if (event.origin !== window.location.origin) return;
                var gen = ++self.hostSkeletonGen;
                self.hostSkeleton = null;
                var contentWidth = (typeof d.contentWidth === 'number' && d.contentWidth > 0) ? d.contentWidth : 0;
                fetch(d.url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
                    .then(function (r) { return r.ok ? r.json() : null; })
                    .then(function (data) {
                        if (data && data.html && gen === self.hostSkeletonGen) {
                            data.contentWidth = contentWidth;
                            self.hostSkeleton = data;
                            // Valid only for the navigation that requested it.
                            self.hostSkeletonExpires = Date.now() + 10000;
                            self.paintHostSkeleton();
                        }
                    })
                    .catch(function () { /* fall back to the plain spinner */ });
                return;
            }

            if (d.type !== 'dcms-loader') return;
            if (d.action === 'show') {
                // postMessage delivery is async, so by now the iframe's
                // beforeunload has already painted the overlay — paint a pending
                // skeleton into it.
                self.paintHostSkeleton();
            } else if (d.action === 'hide') {
                self.hostSkeleton = null;
                self.hostSkeletonGen++;
            }
        });

        document.addEventListener('keydown', function (e) {
            if (e.key !== 'Escape' && e.key !== 'Esc') return;
            var overlay = document.getElementById('dcmsPageLoadingOverlay');
            // Deferred-skeleton overlays ONLY (tagged by paintHostSkeleton).
            // The plain legacy spinner must stay untouched by Escape — anything
            // else would change behaviour on pages with no deferred opt-in.
            if (!overlay || overlay.getAttribute('data-dcms-skeleton') !== '1') return;
            self.hostSkeleton = null;
            self.hostSkeletonGen++;
            // Route through the standard 'hide' handling so listeners in this
            // window (search.tpl) also restore the content iframe's opacity.
            try {
                window.postMessage({ type: 'dcms-loader', action: 'hide' }, window.location.origin);
            } catch (err) { /* ignore */ }
            if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
            DCMSLoader.log('Skeleton overlay dismissed via Escape');
        });
    }

    /**
     * Paints the pending skeleton into the loading overlay in THIS window.
     * No-op until both exist; safe to call from both the fetch completion and
     * the 'show' message (whichever fires last wins).
     */
    paintHostSkeleton() {
        var data = this.hostSkeleton;
        if (!data || Date.now() > this.hostSkeletonExpires) return;

        var overlay = document.getElementById('dcmsPageLoadingOverlay');
        if (!overlay || overlay.getAttribute('data-dcms-skeleton') === '1') return;

        if (data.head && !document.getElementById('dcmsSkeletonCss')) {
            var holder = document.createElement('div');
            holder.id = 'dcmsSkeletonCss';
            holder.innerHTML = data.head;
            document.head.appendChild(holder);
        }

        overlay.setAttribute('data-dcms-skeleton', '1');
        // the skeleton is the loading picture of this page — no animated background over it
        if (overlay.dcmsModalLoader) {
            overlay.dcmsModalLoader.hide();
            overlay.dcmsModalLoader = null;
        }

        // The skeleton box is positioned to exactly cover the content iframe —
        // the Master layout has a sidebar and fixed/fluid widths, so aligning to
        // the full viewport would give the skeleton a different geometry than
        // the real form. The box itself is opaque (hides the loading form); the
        // overlay backdrop outside it keeps the translucent veil, so the top bar
        // stays visible.
        var isDark = document.documentElement.classList.contains('dark');
        var box = 'top:0;left:0;right:0;bottom:0;';
        // Two separate lookups on purpose: a selector LIST has no priority (it
        // returns the first match in document order), so 'a, b' would just mean
        // "any iframe". Prefer the dataset content iframe, then fall back.
        var contentIframe = document.querySelector('iframe[src*="dcmsiframe"]')
            || document.querySelector('iframe');
        if (contentIframe) {
            try {
                var r = contentIframe.getBoundingClientRect();
                box = 'top:' + Math.max(0, Math.round(r.top)) + 'px;'
                    + 'left:' + Math.round(r.left) + 'px;'
                    + 'width:' + Math.round(r.width) + 'px;bottom:0;';
            } catch (e) { /* keep full-viewport box */ }
        }
        // The list page measured its #dcmsContainer (same fixed/fluid mode and
        // iframe as the incoming form) — give the skeleton page that exact
        // width so its geometry (incl. the container-query two-column aside)
        // matches the real form; without it the page falls back to full box.
        var pageWidth = (data.contentWidth && data.contentWidth > 0)
            ? 'width:' + Math.round(data.contentWidth) + 'px;max-width:100%;'
            : '';
        overlay.innerHTML = '<div style="position:absolute;' + box + 'overflow:hidden;'
            + 'background:' + (isDark ? '#111827' : '#f9fafb') + ';">'
            + '<div style="' + pageWidth + 'margin:0 auto;height:100%;">'
            + data.html + '</div>'
            + '</div>';
        DCMSLoader.log('Skeleton painted into loading overlay');
    }

    /**
     * Immediate loading overlay on the current page — fullpage spinner.
     *
     * Intercepts:
     * 1. Form submits (Save, Add, Save and continue, Delete)
     * 2. Clicks on navigation links (Back, table row action buttons)
     *
     * The overlay covers the whole page (body) and blocks further clicks.
     * It disappears automatically once the browser loads a new page (as the current page unmounts).
     */
    initInlineLoadingOverlay() {
        // The overlay is shown in beforeunload — a guarantee that the browser is REALLY navigating.
        // We don't react to clicks directly (that could falsely show the spinner
        // for AJAX requests, preventDefault, etc.).
        // The handler is in initIframeMode() → beforeunload → showPageOverlay().
    }

    /**
     * Creates and shows a fullpage spinner overlay on body.
     * The overlay disappears automatically on navigation (the page unmounts).
     */
    showPageOverlay() {
        // Choose the target document — prefer the parent window (full screen)
        var doc = document;
        try {
            if (window.parent && window.parent !== window && window.parent.document) {
                doc = window.parent.document;
            }
        } catch (e) {
            // Cross-origin — stay in the iframe
        }

        // Don't add it twice
        if (doc.getElementById('dcmsPageLoadingOverlay')) return;

        var isDark = doc.documentElement.classList.contains('dark') || doc.body.classList.contains('dark');

        var overlay = doc.createElement('div');
        overlay.id = 'dcmsPageLoadingOverlay';
        overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;display:flex;justify-content:center;align-items:center;'
            + 'background:' + (isDark ? 'rgba(23,23,23,0.85)' : 'rgba(255,255,255,0.7)')
            + ';opacity:0;transition:opacity 0.15s ease;pointer-events:all;';

        overlay.innerHTML = '<span class="material-symbols-rounded" style="font-size:64px;animation:dcms-spin 1s linear infinite;color:' + (isDark ? '#a3a3a3' : '#6b7280') + ';">progress_activity</span>';

        // Add keyframes if they don't exist
        if (!doc.getElementById('dcmsSpinKeyframes')) {
            var style = doc.createElement('style');
            style.id = 'dcmsSpinKeyframes';
            style.textContent = '@keyframes dcms-spin{to{transform:rotate(360deg)}}';
            doc.head.appendChild(style);
        }

        doc.body.appendChild(overlay);

        // Animated loading background over the overlay (dcms-modal-loader.js, DCMS.ini [modalLoader]
        // page.*). The loader of the overlay's window runs it, so its timers outlive this page; it ends
        // when the overlay is removed (or hidden by the skeleton of a deferred page, paintHostSkeleton()).
        try {
            var hostWindow = doc.defaultView;
            if (hostWindow && hostWindow.DCMS2 && hostWindow.DCMS2.modalLoader) {
                overlay.dcmsModalLoader = hostWindow.DCMS2.modalLoader.show(overlay, { surface: 'page' });
            }
        } catch (e) {
            // the overlay works without it
        }

        // Fade in
        requestAnimationFrame(function() {
            overlay.style.opacity = '1';
        });

        DCMSLoader.log('Page loading overlay shown in ' + (doc === document ? 'iframe' : 'parent'));
    }

    /**
     * Removes the loading overlay from the parent window (or iframe).
     * Called after DOMContentLoaded in the iframe — means the new page has loaded.
     */
    removePageOverlay() {
        var doc = document;
        try {
            if (window.parent && window.parent !== window && window.parent.document) {
                doc = window.parent.document;
            }
        } catch (e) {}

        var overlay = doc.getElementById('dcmsPageLoadingOverlay');
        if (overlay) {
            overlay.style.opacity = '0';
            setTimeout(function() {
                if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
            }, 200);
            DCMSLoader.log('Page loading overlay removed');
        }
    }

    /**
     * Hard-removes a stuck #dcmsPageLoadingOverlay from every same-origin document reachable
     * from here (current iframe, parent, top). Unlike removePageOverlay() this skips the fade
     * and scans the whole frame chain, because on modal-closing we do not know which document
     * a racing beforeunload painted the overlay into. Defensive: same-origin guarded, never throws.
     */
    clearStuckOverlay() {
        var docs = [document];
        try { if (window.parent && window.parent !== window) docs.push(window.parent.document); } catch (e) { /* cross-origin */ }
        try { if (window.top && window.top !== window && window.top !== window.parent) docs.push(window.top.document); } catch (e) { /* cross-origin */ }
        docs.forEach(function (doc) {
            var o = doc && doc.getElementById('dcmsPageLoadingOverlay');
            if (o && o.parentNode) { o.parentNode.removeChild(o); }
        });
    }

    /**
     * Resets the parent window's scroll after navigation inside the content iframe.
     *
     * With iframe-resizer active, the iframe has the document's full height and
     * SCROLLS THE PARENT WINDOW — navigation inside the iframe (list → edit, back,
     * pagination) swaps the document in the iframe, but the parent's scroll
     * position remains. The new view therefore opens "halfway down the page"
     * instead of from the top, as a regular browser navigation would.
     *
     * The parent won't scroll itself: its iFrameResize() configuration blocks
     * scroll requests from children (onScroll → false), so the child scrolls
     * window.parent directly — this is always the same domain (the admin shell).
     *
     * The window.frameElement guard limits the reset to the main content iframe
     * (.iframeContent) — modal iframes must not scroll the page behind the modal
     * (relevant in dcms-scroll-fallback mode, where the content page scrolls
     * itself). Cross-origin embed (frameElement throws) → no reset.
     */
    resetParentScroll() {
        try {
            var fe = window.frameElement;
            if (!fe || !fe.classList.contains('iframeContent')) return;
            if (window.parent && window.parent !== window) {
                window.parent.scrollTo(0, 0);
                DCMSLoader.log('Parent window scrolled to top after iframe navigation');
            }
        } catch (e) {
            // Cross-origin parent — don't touch the foreign host's scroll
        }
    }

    /**
     * Deferred-render: drop the iframe height carry-over after a list → edit
     * navigation.
     *
     * With deferred-render, the parent paints a skeleton overlay "positioned to
     * exactly cover the content iframe" — i.e. with the height of the PREVIOUS
     * page (e.g. a long list with an expanded search). iframe-resizer initializes
     * at that height, and after swapping in the actual form it does NOT recompute
     * it on its own: the iframe is left with the previous view's height — empty
     * space is left under a shorter form (and clipping with a longer one).
     *
     * We do this WHILE the skeleton (overlay) is still visible, so individual
     * corrections are invisible — no interface jump/flicker:
     *
     *  1. We measure the real content height in a loop. After EVERY change we call
     *     parentIFrame.reset() — iframe-resizer recalculates the iframe against the
     *     current content (shrinks the skeleton's carry-over, grows with a
     *     growing editor that renders AFTER window.load and whose autoResize
     *     alone can't catch up). size() is not enough here — it is only a hint and
     *     iframe-resizer ignores it when shrinking; reset() recalculates for real.
     *  2. When the height stops changing (content stable) or the safety timeout
     *     elapses, we call onSettled — only THEN does the host hide the overlay,
     *     revealing an iframe whose height is already correctly measured.
     *
     * parentIFrame is sometimes set asynchronously (postMessage handshake) —
     * if it's not there yet we retry briefly; when iframe-resizer isn't working
     * (e.g. cross-origin host), we simply reveal without correction.
     *
     * @param {function} [onSettled] called once the height is stable
     *        (the point at which to remove the overlay).
     */
    resyncDeferredHeight(onSettled, attempt) {
        attempt = attempt || 0;
        var self = this, pif;
        try { pif = window.parentIFrame; } catch (e) { if (onSettled) onSettled(); return; }
        if (!pif || typeof pif.reset !== 'function') {
            if (attempt < 12) {
                setTimeout(function () { self.resyncDeferredHeight(onSettled, attempt + 1); }, 150);
            } else if (onSettled) {
                onSettled();   // iframe-resizer inactive — reveal without correction
            }
            return;
        }
        var last = -1, stable = 0, ticks = 0, done = false;
        var measure = function () {
            return Math.max(
                document.documentElement.scrollHeight,
                document.body ? document.body.scrollHeight : 0
            );
        };
        var tick = function () {
            var h = measure();
            if (h !== last) { try { pif.reset(); } catch (e) {} last = h; stable = 0; }
            else { stable++; }
            ticks++;
            // stable: ~3×120 ms without changes; safety timeout: ~6 s (heavy editor).
            if (stable >= 3 || ticks >= 50) {
                if (!done) {
                    done = true;
                    DCMSLoader.log('Deferred height settled at ' + last + 'px (parentIFrame.reset)');
                    // reset is async (host↔iframe round-trip) — reveal the overlay
                    // after a short delay, once the iframe has already computed its height.
                    setTimeout(function () { if (onSettled) onSettled(); }, 180);
                }
                return;
            }
            setTimeout(tick, 120);
        };
        tick();
    }

    /**
     * Standard navigation (without deferred-render): drop the iframe height
     * carry-over after a full document navigation (e.g. long list → short edit form).
     *
     * iframe-resizer with the 'lowestElement' method does NOT zero the iframe
     * height on the load event (resetRequiredMethods does not include
     * 'lowestElement' on either the host or the child side) — the new document is
     * measured in a viewport with the height of the PREVIOUS page, and any
     * element whose height depends on the viewport locks in the old height (a
     * ratchet: it can grow, not shrink). parentIFrame.reset() breaks the loop:
     * the child momentarily measures bodyOffset (body has height:auto), and the
     * host accepts the result without a tolerance threshold.
     *
     * Unlike resyncDeferredHeight, this variant runs IN THE BACKGROUND and does
     * NOT gate the overlay (which still comes down immediately on
     * DOMContentLoaded, as before): a single reset at the start does what
     * iframe-resizer does natively on load for methods in resetRequiredMethods;
     * subsequent resets only happen when content changes (growing components).
     *
     * Anti-compounding safety net: if the page contains an element whose height
     * is coupled to the viewport (e.g. a min-h-screen wrapper — reset bypasses
     * the tolerance, so every correction bumps the measurement by a constant
     * small delta), the loop won't converge. We detect the signature — several
     * consecutive INCREMENTS of nearly identical, small step size — and abort;
     * real content never grows that way.
     */
    resyncNavigationHeight(attempt) {
        attempt = attempt || 0;
        var self = this, pif;
        try { pif = window.parentIFrame; } catch (e) { return; }
        if (!pif || typeof pif.reset !== 'function') {
            // The iframe-resizer handshake is sometimes async; without a host (an
            // iziModal modal, a page opened directly) — after a few attempts just give up.
            if (attempt < 12) {
                setTimeout(function () { self.resyncNavigationHeight(attempt + 1); }, 150);
            }
            return;
        }
        var last = -1, stable = 0, ticks = 0;
        var upSteps = [];   // window of recent increments (feedback-loop detection)
        var measure = function () {
            return Math.max(
                document.documentElement.scrollHeight,
                document.body ? document.body.scrollHeight : 0
            );
        };
        var tick = function () {
            var h = measure();
            if (h !== last) {
                if (last > -1 && h > last) {
                    upSteps.push(h - last);
                    if (upSteps.length > 4) upSteps.shift();
                    if (upSteps.length === 4) {
                        var mx = Math.max.apply(null, upSteps);
                        var mn = Math.min.apply(null, upSteps);
                        if (mx <= 128 && (mx - mn) <= 8) {
                            DCMSLoader.log('resyncNavigationHeight aborted — viewport feedback loop signature (steps ' + upSteps.join(',') + 'px)');
                            return;
                        }
                    }
                } else {
                    upSteps = [];
                }
                try { pif.reset(); } catch (e) {}
                last = h; stable = 0;
            } else {
                stable++;
                upSteps = [];
            }
            ticks++;
            // stable: ~3×120 ms without changes; safety timeout: ~6 s (heavy editor).
            if (stable >= 3 || ticks >= 50) {
                DCMSLoader.log('Navigation height settled at ' + last + 'px (parentIFrame.reset)');
                return;
            }
            setTimeout(tick, 120);
        };
        tick();
    }

    /**
     * Height of this document as reported to the host by iframe-resizer.
     *
     * The host asks for 'lowestElement' (main.js): the library's version takes the
     * largest getBoundingClientRect().bottom of EVERY element under body. Two kinds
     * of elements report a bottom far below anything visible and stretched the
     * iframe with empty space:
     *
     *  - children of <select> — Chrome lays out the options of a multiple select as
     *    blocks below the box, also when the select is hidden by select2
     *    (select2-hidden-accessible: 1 px, overflow hidden). A select with 3000
     *    options added ~60 000 px;
     *  - the content of a closed <details> (collapsed panels, e.g.
     *    collapsible('narrow') on a phone) — getBoundingClientRect() lays the hidden
     *    content out as if the panel were open.
     *
     * The method below is the library's 'lowestElement' (iframe-resizer 4.4.5:
     * max(bodyOffset || documentElementOffset, bottom + margin-bottom of each
     * element)) that skips both subtrees; the <select> and <summary> boxes still
     * count. iframe-resizer reads window.iFrameResizer when the host initialises
     * the frame (after the load event), so setting it here is in time. A page that
     * sets its own method (e.g. search.tpl: 'documentElementOffset') keeps it.
     */
    installHeightCalculation() {
        var cfg = window.iFrameResizer = window.iFrameResizer || {};
        if (typeof cfg !== 'object' || cfg.heightCalculationMethod !== undefined) return;

        var px = function (value) {
            var n = parseInt(value, 10);
            return isNaN(n) ? 0 : n;
        };
        var filter = {
            acceptNode: function (el) {
                var parent = el.parentElement;
                if (el.tagName === 'OPTION' || el.tagName === 'OPTGROUP'
                    || (parent && parent.tagName === 'SELECT')) {
                    return NodeFilter.FILTER_REJECT;
                }
                if (parent && parent.tagName === 'DETAILS' && !parent.open && el.tagName !== 'SUMMARY') {
                    return NodeFilter.FILTER_REJECT;
                }
                return NodeFilter.FILTER_ACCEPT;
            }
        };

        cfg.heightCalculationMethod = function () {
            var body = document.body;
            if (!body) return document.documentElement.offsetHeight;
            var bodyStyle = window.getComputedStyle(body);
            var height = (body.offsetHeight + px(bodyStyle.marginTop) + px(bodyStyle.marginBottom))
                || document.documentElement.offsetHeight;
            var walker = document.createTreeWalker(body, NodeFilter.SHOW_ELEMENT, filter);
            var el, bottom;
            while ((el = walker.nextNode())) {
                bottom = el.getBoundingClientRect().bottom + px(window.getComputedStyle(el).marginBottom);
                if (bottom > height) height = bottom;
            }
            return height;
        };
    }

    /**
     * Enables scrolling when the page is in an iframe without iframe-resizer.
     *
     * window.parentIFrame is set ASYNCHRONOUSLY — after the
     * iframeResizer.contentWindow.js ↔ parent handshake (postMessage round-trip).
     * It cannot be checked right after DOMContentLoaded.
     * We check a few times with increasing delay.
     */
    enableScrollIfNoIframeResizer() {
        if (!document.body.classList.contains('dcms-iframe-content')) return;

        // If already available — ok, do nothing
        if (window.parentIFrame) return;

        var attempts = 0;
        var maxAttempts = 5;
        var checkInterval = setInterval(function() {
            attempts++;
            if (window.parentIFrame) {
                clearInterval(checkInterval);
                DCMSLoader.log('iframeResizer initialized after ' + attempts + ' checks');
                return;
            }
            if (attempts >= maxAttempts) {
                clearInterval(checkInterval);
                // After ~2.5s without parentIFrame — iframe-resizer really isn't working.
                // We use a CLASS instead of inline style — inline style overrides the CSS overflow:hidden
                // and causes documentElement.scrollHeight to inflate → a scrollbar in the parent window.
                document.body.classList.add('dcms-scroll-fallback');
                DCMSLoader.log('Enabled scroll for iframe without iframe-resizer (after ' + maxAttempts + ' checks)');
            }
        }, 500);
    }

    /**
     * Sends a message to the parent window.
     * The parent (search.tpl) listens and shows/hides the inline loader.
     */
    notifyParent(action, extra) {
        try {
            if (window.parent && window.parent !== window) {
                window.parent.postMessage(Object.assign({
                    type: 'dcms-loader',
                    action: action
                }, extra || {}), '*');
                DCMSLoader.log(`Notified parent: ${action}`);
            }
        } catch (err) {
            DCMSLoader.log(`Error notifying parent: ${err.message}`);
        }
    }

    /**
     * Shows the modal spinner (showModalSpinner).
     * ONLY for manual invocation — it is not wired to any automatic events.
     */
    showSpinner() {
        try {
            const targetWindow = this.findHighestAppWindow();
            if (targetWindow && targetWindow.APP && targetWindow.APP.modalsInstance) {
                const loadingText = typeof targetWindow.__i18n === 'function'
                    ? targetWindow.__i18n('loadingData', 'commonjs', 'Trwa odczyt danych ...')
                    : 'Trwa odczyt danych ...';
                targetWindow.APP.modalsInstance.showModalSpinner(loadingText);
                DCMSLoader.log('Spinner shown via APP.modalsInstance');
            }
        } catch (err) {
            DCMSLoader.log(`Error showing spinner: ${err.message}`);
        }
    }

    /**
     * Hides the modal spinner (hideModalSpinner).
     * ONLY for manual invocation.
     */
    hideSpinner() {
        try {
            const targetWindow = this.findHighestAppWindow();
            if (targetWindow && targetWindow.APP && targetWindow.APP.modalsInstance) {
                targetWindow.APP.modalsInstance.hideModalSpinner();
                DCMSLoader.log('Spinner hidden via APP.modalsInstance');
            }
        } catch (err) {
            DCMSLoader.log(`Error hiding spinner: ${err.message}`);
        }
    }

    /**
     * Finds the highest window with an APP object
     */
    findHighestAppWindow() {
        let currentWindow = window;
        let highestWindow = currentWindow;

        if (currentWindow.APP) {
            highestWindow = currentWindow;
        }

        try {
            while (currentWindow !== window.top) {
                currentWindow = currentWindow.parent;
                if (currentWindow.APP) {
                    highestWindow = currentWindow;
                }
            }
        } catch (err) {
            // Cross-origin
        }

        return highestWindow;
    }

    /**
     * Static logging method
     */
    static log(message) {
        if (typeof window.DCMSDebugLog === 'function') {
            window.DCMSDebugLog(message, 3, 'DCMSLoader');
        } else {
            console.log(`[DCMSLoader]: ${message}`);
        }
    }
}

// Initialization
window.DCMSLoader = new DCMSLoader();
DCMSLoader.log('DCMSLoader created, isInIframe: ' + (window.self !== window.top));

// Export for ES6 modules
export { DCMSLoader };
